import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { DocumentDownloadService } from '../../services/document-download.service';
import { DownloadFileService } from '../../services/download-file.service';
import { PrintFileService } from '../../services/print-file.service';

@Component({
    selector: 'apcam-img-viewer',
    templateUrl: './img-viewer.component.html',
    styleUrls: ['./img-viewer.component.scss'],
})
export class ImgViewerComponent implements OnInit, OnChanges {
    /**
     * URL of the image to be displayed.
     */
    @Input()
    imgUrl: string = '';

    /**
     * Content type of the image.
     */
    @Input()
    contentType: string = '';

    /**
     * Subject for triggering download actions.
     */
    @Input()
    download: Subject<void> = new Subject();

    /**
     * Name of the file to be downloaded.
     */
    @Input()
    fileName: string = '';

    /**
     * Subject for triggering print actions.
     */
    @Input()
    print: Subject<void> = new Subject();

    /**
     * Event emitted when the image is loaded.
     */
    @Output()
    imgLoaded: EventEmitter<boolean> = new EventEmitter();

    blobUrl: string = '';

    sanitizedBlobUrl: SafeUrl = '';

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly documentDownloadService: DocumentDownloadService,
        private readonly downloadFileService: DownloadFileService,
        private readonly printFileService: PrintFileService,
        private readonly sanitizer: DomSanitizer
    ) {}

    ngOnInit(): void {
        if (this.imgUrl.length == 0 || this.contentType.length == 0) return;
        this.loadFile();

        this.download
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.downloadImg();
            });

        this.print.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.printImg();
        });
    }

    ngOnChanges() {
        if (this.imgUrl.length == 0 || this.contentType.length == 0) return;
        this.loadFile();
    }
    /**
     * Downloads the image
     *
     * @returns This method does not return a value.
     */
    private downloadImg(): void {
        this.downloadFileService.downloadFileFromUrl(
            this.blobUrl,
            this.fileName
        );
    }

    /**
     * Prints the image
     *
     * @returns This method does not return a value.
     */
    private printImg(): void {
        this.printFileService.printFileFromUrl(
            this.blobUrl,
            '.container-image-preview'
        );
    }

    /**
     * Loads the image file as a blob URL and sanitizes it.
     *
     * @returns This method does not return a value.
     */
    private loadFile(): void {
        this.documentDownloadService
            .downloadFileToBlobUrl(this.imgUrl, this.contentType)
            .then((url) => {
                this.blobUrl = url;
                this.sanitizedBlobUrl = this.sanitize(url);
                this.imgLoaded.emit(true);
            });
    }

    /**
     * Sanitizes the provided URL to bypass security restrictions.
     *
     * @param url - The URL to be sanitized.
     * @returns The sanitized URL.
     */
    private sanitize(url: string): SafeUrl {
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }
}
